import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../../components/Settings/SettingsPageContainer';
import {useRPC} from '../../../../../hooks/rpc';
import {useMessage} from '../../../../../hooks/message';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import './index.scss';
import {RawDevice} from '../../../../../store/setup/state';
import {useSetSelector} from '../../../../../hooks/set-selector';
import {onClickHandler} from '../../../../../utils/onclick';
import {BridgeDeviceModel} from '../../../../../types/bridge-device';
import {Select, SelectItem} from '../../../../../components/Select';
import {addRawDeviceAction, clearRawDevicesAction, removeRawDeviceAction} from '../../../../../store/setup/actions';
import {setVersionAction} from '../../../../../store/control/actions';
import {SetupIOTestComponent} from '../SetupIOTestComponent';
import {useFilter} from '../../../../../hooks/filter';
import {useFilterSelector} from '../../../../../hooks/filter-selector';
import {
    BRIDGE_DEVICE_TYPE_ID_INO_PAN,
    BRIDGE_DEVICE_TYPE_ID_INO_PAN_2,
    BRIDGE_DEVICE_TYPE_ID_INO_PAN_3,
} from '../../../../../types/control';
import {useSortBySelector} from '../../../../../hooks/sort-by-selector';
import {useQuestion} from '../../../../../hooks/question';
import {useLocalization} from "../../../../../hooks/localization";

const BACK_URL = '/settings/gateway';

const MatchedItemButton = memo(function MatchedItemButton({color, text, onClick, disabled}: { color: string; text: string; onClick: () => void; disabled?: boolean }) {
    const {t} = useLocalization();
    return (
        <div className={'matched-item-button ' + color} {...onClickHandler(disabled ? null : onClick)} >
            <span dangerouslySetInnerHTML={{__html: disabled ? t('searching') : text}}/>
        </div>
    );
});

type MatchedItemProps = {
    deviceTypeName: string;
    deviceImage: string;
    deviceName: string;
    inputCount: number;
    outputCount: number;
    bridgeDeviceList: BridgeDeviceModel[];
    ioTestClick: () => void;
    deviceId?: string;
    onDeviceReset: () => void;
    onDeviceMatch: () => void;
    searchInProgress: boolean;
} & RawDevice;

const MatchedItem = memo(function MatchedItem(props: MatchedItemProps) {
    const {t} = useLocalization();
    const {success, failure, info} = useMessage();
    const [rpc] = useRPC();
    const dispatch = useDispatch();
    const {
        gateway_id,
        unique_device_id,
        device_type_id,
        rf_power,
        deviceTypeName,
        deviceImage,
        deviceName,
        bridgeDeviceList,
        ioTestClick,
        deviceId,
        onDeviceReset,
        onDeviceMatch,
        inputCount,
        outputCount,
        searchInProgress,
    } = props;

    const [findInProgress, setFindInProgress] = useState(false);
    const [selectedBridgeDevice, setSelectedBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(null);
    const [matchInProgress, setMatchInProgress] = useState(false);

    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMemo(() =>
            bridgeDeviceList
                .filter(bridgeDevice => bridgeDevice.gateway_id === gateway_id && bridgeDevice.type_id === device_type_id)
                .map(bridgeDevice => ({value: bridgeDevice, label: bridgeDevice.name})),
        [gateway_id, device_type_id, bridgeDeviceList],
    );

    const onFindClick = useCallback(() => {
        if (matchInProgress) {
            return;
        }

        setFindInProgress(true);
        rpc.findDevice(gateway_id, unique_device_id, false)
            .catch(failure);
    }, [rpc, matchInProgress, gateway_id, unique_device_id, failure]);

    const onStopFindClick = useCallback(() => {
        if (matchInProgress) {
            return;
        }

        setFindInProgress(false);
        rpc.findDevice(gateway_id, unique_device_id, true)
            .catch(failure);
    }, [rpc, matchInProgress, gateway_id, unique_device_id, failure]);

    const onResetClick = useCallback(() => {
        if (matchInProgress) {
            return;
        }

        rpc.resetDevice(gateway_id, unique_device_id)
            .then((message: string) => {
                onDeviceReset();
                success(message);
            })
            .catch(failure);
    }, [matchInProgress, rpc, gateway_id, unique_device_id, success, failure, onDeviceReset]);

    const onIOTestClick = useCallback(() => {
        if (matchInProgress) {
            return;
        }

        ioTestClick();
    }, [ioTestClick, matchInProgress]);

    const onMatchClick = useCallback(() => {
        if (matchInProgress) {
            return;
        }

        if (selectedBridgeDevice?.value == null) {
            failure(t('pls_select_device'));

            return;
        }

        setMatchInProgress(true);
        rpc.matchDevice(gateway_id, unique_device_id, selectedBridgeDevice?.value?.id)
            .then(onDeviceMatch)
            .catch(failure)
            .finally(() => setMatchInProgress(false));
    }, [t, failure, gateway_id, matchInProgress, rpc, selectedBridgeDevice?.value, unique_device_id, onDeviceMatch]);

    const onReMatchClick = useCallback(() => {
        if (matchInProgress) {
            return;
        }

        setMatchInProgress(true);
        rpc.reMatchDevice(gateway_id, unique_device_id, deviceId)
            .catch(failure)
            .finally(() => setMatchInProgress(false));
    }, [matchInProgress, rpc, gateway_id, failure, unique_device_id, deviceId]);

    const onClickHandleCopy = useCallback(() => {
        if ("clipboard" in navigator) {
            return navigator.clipboard.writeText(unique_device_id)
        } else {
            return document.execCommand("copy", true, unique_device_id)
        }
    }, [unique_device_id])

    return (
        <div className='matched-item-row line'>
            <div className='left column'>
                <div className='image-and-name'>
                    <img className='image' src={deviceImage} alt=''/>
                    <span className='s-device-type-name-label'>{deviceTypeName}</span>
                </div>
                <div className='info-container'>
                    {
                        renderIf(unique_device_id)(() =>
                            <>
                                <span onClick={onClickHandleCopy}><span className='label-text'>{t('unique_id')}:</span> {unique_device_id}</span>
                                <span><span className='label-text'>{t('device_name')}:</span> {deviceName}</span>
                                <span><span className='label-text'>{t('rf_power')}:</span> {rf_power.toString()}</span>
                            </>, () =>
                            <span><span className='label-text'>{t('device_name')}:</span> {deviceName}</span>,
                        )
                    }
                </div>
            </div>
            <div className='right column'>
                <div className='select-container'>
                    {
                        renderIf(deviceName.length === 0)(() =>
                            <Select
                                options={bridgeDeviceOptionList}
                                onChange={setSelectedBridgeDevice}
                                value={selectedBridgeDevice}/>,
                        )
                    }
                </div>
                <div className={'button-list-container' /*+(unique_device_id.length === 0 ? ' reverse' : '')*/}>
                    {
                        renderIf(unique_device_id.length > 0 || ((deviceTypeName === 'PAN19xx' || deviceTypeName === 'PAN1707')))(() =>
                            <>
                            {
                                renderIf(deviceTypeName !== 'PAN19xx' && deviceTypeName !== 'PAN1707')(()=> (
                                <>
                                {
                                    renderIf(findInProgress)(() =>
                                        <MatchedItemButton
                                            color='green'
                                            text={t('stop')}
                                            onClick={onStopFindClick}/>, () =>
                                        <MatchedItemButton
                                            color='blue'
                                            text={t('find')}
                                            onClick={onFindClick}/>,
                                    )
                                }
                                <MatchedItemButton
                                    color='red'
                                    text={t('reset')}
                                    onClick={onResetClick}/>
                                </>
                                ))
                            }
                                {
                                    renderIf(inputCount > 0 || outputCount > 0)(() => (
                                            <MatchedItemButton
                                                color='orange'
                                                text={t('io_test')}
                                                onClick={onIOTestClick}/>
                                        ), () => (<div style={{width: 95}}/>),
                                    )
                                }
                            </>,
                        )
                    }
                    {
                        renderIf(deviceName.length === 0)(() =>
                                <MatchedItemButton
                                    color='green'
                                    text={matchInProgress ? t('matching') : t('match')}
                                    onClick={onMatchClick}
                                    disabled={searchInProgress}/>,
                            () =>
                                <MatchedItemButton
                                    color='green'
                                    text={matchInProgress ? t('matching') : t('again') + '<br/>' + t('match')}
                                    onClick={onReMatchClick}
                                    disabled={searchInProgress}/>,
                        )
                    }
                </div>
            </div>
        </div>
    );
});

export const SetupMatchScreen = memo(function SetupMatchScreen() {
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const [rpc] = useRPC();
    const {failure} = useMessage();
    const dispatch = useDispatch();
    const question = useQuestion();

    const panelList = useFilterSelector(
        state => state.control.bridgeDeviceList,
        bridgeDevice => bridgeDevice.type_id === BRIDGE_DEVICE_TYPE_ID_INO_PAN || bridgeDevice.type_id === BRIDGE_DEVICE_TYPE_ID_INO_PAN_2 || bridgeDevice.type_id === BRIDGE_DEVICE_TYPE_ID_INO_PAN_3,
        []);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);
    const filteredBridgeDeviceList = useFilter(
        bridgeDeviceList,
        bridgeDevice => bridgeDevice.unique_device_id == null || bridgeDevice.unique_device_id.length === 0,
        []);
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);

    const [manualSearchInProgress, setManualSearchInProgress] = useState(false);
    const [automaticSearchInProgress, setAutomaticSearchInProgress] = useState(false);
    const [ioTestModalRawDevice, setIoTestModalRawDevice] = useState<RawDevice>(null);
    //const [sortByRfPower, setSortByRfPower] = useState(true);

    const manualSearchText = useMemo(() =>
        manualSearchInProgress ? 'manual_searching' : 'manual_search', [manualSearchInProgress]);

    const automaticSearchText = useMemo(() =>
        automaticSearchInProgress ? 'automatic_searching' : 'automatic_search', [automaticSearchInProgress]);

    const gatewayList = useSelector(state => state.control.gatewayList);

    const selectedGateway = useMemo(() => gatewayList.find(gateway => gateway.id === params?.id) || null, [gatewayList, params?.id]);

    const rawDeviceList = useFilter(useSelector(state => state.setup.rawDeviceList), i => i.gateway_id === selectedGateway?.id);

    //const rawDeviceList = useFilter(useSortBySelector(state => state.setup.rawDeviceList, sortByRfPower ? () => {console.log('Line true',sortByRfPower);return null} : () => (r => {console.log('Line false',sortByRfPower);return r.unique_device_id ? -r.rf_power : -999}), [sortByRfPower]), i => i.gateway_id === selectedGateway?.id);

    const setupMode = useMemo(() => selectedGateway?.settings?.setup_mode || false, [selectedGateway?.settings?.setup_mode]);

    const searchInProgress = useMemo(() => manualSearchInProgress || automaticSearchInProgress,
        [manualSearchInProgress, automaticSearchInProgress]);

    const changeSetupMode = useCallback((setupMode: boolean) => {
        if (searchInProgress) {
            return;
        }

        dispatch(clearRawDevicesAction());

        const job = () => {
            rpc.updateGateway({
                ...selectedGateway,
                settings: {
                    ...selectedGateway?.settings,
                    setup_mode: setupMode,
                },
            }).catch(failure);
        };

        if (setupMode && gatewayList.some(gateway => gateway.id !== selectedGateway.id && gateway.settings.setup_mode)) {
            question(t('there_can_be_only_one_gateway_setup_mode__do_you_want_get_other_gateways_to_home_mode'), () => {
                job();
            });
            return;
        }

        job();
    }, [t, searchInProgress, dispatch, rpc, selectedGateway, failure, gatewayList, question]);

    const disableSetupModePressed = useCallback(() => {
        changeSetupMode(false);
    }, [changeSetupMode]);

    const enableSetupModePressed = useCallback(() => {
        changeSetupMode(true);
    }, [changeSetupMode]);

    const manualSearchPressed = useCallback(() => {
        if (searchInProgress) {
            return;
        }

        dispatch(clearRawDevicesAction());
        if (panelList.length > 0) {
            dispatch(addRawDeviceAction({
                unique_device_id: '',
                gateway_id: selectedGateway.id,
                device_type_id: panelList[0].type_id,
                rf_power: 0,
            }));
        }

        setManualSearchInProgress(true);
        rpc.scanDevices(selectedGateway?.id, false)
            .catch(failure)
            .finally(() => {
                setManualSearchInProgress(false);
            });
    }, [searchInProgress, dispatch, panelList, rpc, selectedGateway?.id, failure]);

    const automaticSearchPressed = useCallback(() => {
        if (searchInProgress) {
            return;
        }

        dispatch(clearRawDevicesAction());
        if (panelList.length > 0) {
            dispatch(addRawDeviceAction({
                unique_device_id: '',
                gateway_id: selectedGateway.id,
                device_type_id: panelList[0].type_id,
                rf_power: 0,
            }));
        }

        setAutomaticSearchInProgress(true);
        rpc.scanDevices(selectedGateway.id, true)
            .catch(failure)
            .finally(() => {
                setAutomaticSearchInProgress(false);
            });
    }, [searchInProgress, dispatch, panelList, rpc, selectedGateway?.id, failure]);

    const onDeviceReset = useCallback((rawDevice: RawDevice) => {
        if (!setupMode) {
            dispatch(removeRawDeviceAction(rawDevice));
        }
    }, [dispatch, setupMode]);

    const onDeviceMatch = useCallback((rawDevice: RawDevice) => {
        if (setupMode) {
            dispatch(removeRawDeviceAction(rawDevice));
        }
    }, [dispatch, setupMode]);

    /*const onSortedClick = useCallback(() => {
        automaticSearchPressed()
        setSortByRfPower(false)
        console.log(sortByRfPower)
    }, [automaticSearchPressed, sortByRfPower])

    useEffect(() => {
        setSortByRfPower(p => !p)
    }, [])
*/
    const buttons = useMemo(() => [
        /*{
            text: 'SIRALA (RF)',
            onClick: onSortedClick,
            className: 'blue',
        },*/
        {
            renderContent: () => <span>{t('change_mode')}<div style={{color: 'lime'}}>{t('home')}</div></span>,
            onClick: enableSetupModePressed,
            className: 'gray',
            hidden: setupMode,
        },
        {
            renderContent: () => <span>{t('change_mode')}<div style={{color: 'lime'}}>{t('setup')}</div></span>,
            onClick: disableSetupModePressed,
            className: 'gray',
            hidden: !setupMode,
        },
        {
            text: t(manualSearchText),
            onClick: manualSearchPressed,
            className: 'red',
        },
        {
            text: t(automaticSearchText),
            onClick: automaticSearchPressed,
            className: 'green',
        },
    ], [
        t,
        enableSetupModePressed,
        disableSetupModePressed,
        manualSearchPressed,
        manualSearchText,
        automaticSearchPressed,
        automaticSearchText,
        setupMode,
        //onSortedClick
    ]);

    useEffect(() => {
        dispatch(clearRawDevicesAction());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='service' isFullscreen={true}>
            <SettingsPageContainer
                className='setup-match-screen'
                buttons={buttons}
                backUrl={BACK_URL}
                title={selectedGateway?.name || ''}>
                <div className='list-container'>
                    {
                        rawDeviceList.map((item) =>
                            <MatchedItem
                                key={item.unique_device_id}
                                {...item}
                                deviceTypeName={bridgeDeviceTypeSet[item.device_type_id]?.name || '-'}
                                inputCount={bridgeDeviceTypeSet[item.device_type_id]?.input_count}
                                outputCount={bridgeDeviceTypeSet[item.device_type_id]?.output_count}
                                deviceImage={bridgeDeviceTypeSet[item.device_type_id]?.image_set?.default || '-'}
                                deviceName={item.unique_device_id.length === 0 ? selectedGateway.name : bridgeDeviceList.find(bridgeDevice => bridgeDevice.unique_device_id === item.unique_device_id)?.name || ''}
                                deviceId={item.unique_device_id.length === 0 ? panelList.find(p=>p.gateway_id === selectedGateway.id)?.id : bridgeDeviceList.find(bridgeDevice => bridgeDevice.unique_device_id === item.unique_device_id)?.id || ''}
                                bridgeDeviceList={filteredBridgeDeviceList}
                                ioTestClick={() => setIoTestModalRawDevice(item)}
                                onDeviceReset={() => onDeviceReset(item)}
                                onDeviceMatch={() => onDeviceMatch(item)}
                                searchInProgress={searchInProgress}/>,
                        )
                    }
                </div>
            </SettingsPageContainer>
            {
                renderIf(ioTestModalRawDevice != null)(() =>
                    <SetupIOTestComponent
                        onClosePressed={() => setIoTestModalRawDevice(null)}
                        rawDevice={ioTestModalRawDevice}
                        deviceName={bridgeDeviceList.find(bridgeDevice => bridgeDevice.unique_device_id === ioTestModalRawDevice.unique_device_id)?.name || ''}
                        deviceImage={bridgeDeviceTypeSet[ioTestModalRawDevice.device_type_id]?.image_set?.default || '-'}/>,
                )
            }
        </PasswordContainer>
    );
});
